/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useGetCandidacyInfo } from "@/hooks/queries/useGetCandidacyInfo";
import { Row } from "@/components";
// Unchanged shared form piece — still lives with the legacy sheet until that
// folder is deleted.
import { ChangeStatusDialogRequisitionSelect } from "./ChangeStatusDialogRequisitionSelect";
import { FormCurrencyInput } from "@/components/form/FormCurrencyInput";
import { FormDateInput } from "@/components/form/FormDateInput";
import { FormEnumSelect } from "@/components/form/FormEnumSelect";
import { FormNumberInput } from "@/components/form/FormNumberInput";
import { FormNumberSelect } from "@/components/form/FormNumberSelect";
import { FormTextInput } from "@/components/form/FormTextInput";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";

import { useSheetScope } from "@/contexts/SheetScopeContext";
import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { useReportUnexpectedNull } from "@/hooks/useReportUnexpectedNull";
import { useCreatePlacement } from "@/hooks/mutations/useCreatePlacement";
import { useGetRequisitions } from "@/hooks/queries/useGetRequisitions";
import { useAddPlacement } from "@/hooks/sets/useAddPlacement";
import { useSetCandidacyInAllLists } from "@/hooks/sets/useSetCandidacyInAllLists";
import { useSetCandidacyInfo } from "@/hooks/sets/useSetCandidacyInfo";
import {
  defaultRequisitionQueryParams,
  defaultRequisitionStatusesForOffers,
  shiftTypeOptions,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { handleFunctions } from "@/lib/handleFunctions";
import { misc } from "@/lib/misc";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { mutations } from "@/lib/mutations";
import { nullableDateSchema, requiredDateSchema } from "@/lib/schemas";
import {
  CandidacyStatus,
  CompensationType,
  Job,
  PlacementStatus,
  Requisition,
  RequisitionStatus,
  RevenueType,
  ShiftType,
} from "@/types";

const offerSchema = z.object({
  backfillGuaranteeDays: z.coerce
    .number()
    .min(0, "Must be greater than 0")
    .nullable(),
  baseCompensation: z.coerce.number().min(0, "Must be greater than 0"),
  dateAccepted: nullableDateSchema,
  dateOffered: requiredDateSchema,
  dateSetToStart: nullableDateSchema,
  retentionBonusDays: z.coerce
    .number()
    .min(0, "Must be greater than 0")
    .nullable(),
  ownerUserId: z.coerce.number(),
  requisitionId: z.coerce.number(),
  retentionBonusAmount: z.coerce
    .number()
    .min(0, "Must be greater than 0")
    .nullable(),
  revenueEarned: z.coerce.number().min(0, "Must be greater than 0").nullable(),
  shiftDifferential: z.coerce
    .number()
    .min(0, "Must be greater than 0")
    .nullable(),
  shiftType: z.nativeEnum(ShiftType, { message: "Required" }),
  shiftTypeDescription: z.string().nullable(),
});

const schemaToPlacement = ({
  candidacyId,
  data,
  job,
}: {
  candidacyId: string;
  data: z.infer<typeof offerSchema>;
  job: Job;
}) => {
  return {
    ...data,
    candidacyId: candidacyId,
    compensationType: job.compensationType,
    dateAccepted: dates.convertStringDateToIso(data.dateAccepted),
    dateOffered:
      dates.convertStringDateToIso(data.dateOffered) ??
      new Date().toISOString(),
    dateSetToStart: dates.convertStringDateToIso(data.dateSetToStart),
    jobId: job.id,
    status: PlacementStatus.OFFERED,
  };
};

export const OfferDialog = ({
  open,
  onOpenChange,
  onStatusChanged,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStatusChanged?: (status: CandidacyStatus) => void;
}) => {
  /* Contexts */
  const { candidacyId, job } = useSheetScope();
  const { userOptions } = useTaggableUsers();

  /* Database queries */
  // Reads
  const { data: candidacyInfo } = useGetCandidacyInfo({ candidacyId });
  const { data: requisitions, status } = useGetRequisitions({
    jobId: job.id,
    queryOptions: {
      ...defaultRequisitionQueryParams,
      statuses: defaultRequisitionStatusesForOffers,
    },
  });

  // Creates
  const { mutate: createPlacement } = useCreatePlacement();

  /* Cache Updates */
  // Adds
  const addPlacement = useAddPlacement();

  // Sets
  const setCandidacyInAllLists = useSetCandidacyInAllLists();
  const setCandidacyInfo = useSetCandidacyInfo();

  /* React Hooks */
  const [selectedRequisition, setSelectedRequisition] = useState<
    Requisition | undefined
  >(undefined);

  /* Misc Hooks */
  const reportUnexpectedNull = useReportUnexpectedNull();

  const baseCompensation = misc.calculateEstimatedRevenueAmount(
    job.compensationType === CompensationType.HOURLY,
    job.compensationMin,
    job.compensationMax,
    job.percentOfSalary,
  );

  const form = useForm<z.infer<typeof offerSchema>>({
    resolver: zodResolver(
      // Revenue earned is required when the revenue type is percent and the selected requisition is not a backfill
      // Because backfills are for $0 and if revenue type is percent, revenue earned is calculated on job.percentOfSalary and job.compensationMin/Max.
      // Therefore, is revenue earned is null, it's a signal the job is not set up correctly.
      offerSchema.refine(
        (data) =>
          job.revenueType === RevenueType.PERCENT &&
          selectedRequisition?.status !== RequisitionStatus.BACKFILL
            ? !!data.revenueEarned
            : true,
        {
          message:
            "Revenue earned is required when the revenue type is percent. Check job form for missing information.",
          path: ["revenueEarned"],
        },
      ),
    ),
    defaultValues: {
      backfillGuaranteeDays: job.backfillGuaranteeDays || null,
      baseCompensation: baseCompensation === "" ? undefined : baseCompensation,
      dateAccepted: "",
      dateOffered: dates.formatIsoDateForUser(new Date().toISOString()) ?? "",
      dateSetToStart: "",
      retentionBonusDays: selectedRequisition?.retentionBonusDays || null,
      ownerUserId: undefined,
      requisitionId: undefined,
      retentionBonusAmount: selectedRequisition?.retentionBonusAmount || null,
      revenueEarned: null,
      shiftDifferential: null,
      shiftType: undefined,
      shiftTypeDescription: selectedRequisition?.shiftTypeDescription || null,
    },
  });

  if (status === "pending") {
    return null;
  } else if (status === "error") {
    return null;
  }

  const handleSubmit = (data: z.infer<typeof offerSchema>) => {
    mixpanelUtils.trackFormSubmission(
      "Create offer",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
      {
        has_date_accepted: !!data.dateAccepted,
        has_date_set_to_start: !!data.dateSetToStart,
        has_backfill_guarantee: data.backfillGuaranteeDays != null,
      },
    );
    if (!selectedRequisition) {
      reportUnexpectedNull("OfferDialog.handleSubmit", "selectedRequisition");
      return;
    }
    const placementRequest = schemaToPlacement({
      candidacyId,
      data,
      job,
    });
    mutations.createPlacement({
      addPlacement,
      createPlacement,
      placementRequest,
      setCandidacyInfo,
      onSuccess: () => {
        setCandidacyInAllLists({
          candidacyId,
          lastCandidacyStatusChange: {
            createdAt: new Date().toISOString(),
            fromStatus: candidacyInfo?.status ?? null,
            toStatus: CandidacyStatus.OFFER,
            statusChangeReason: null,
            byAgent: false,
          },
        });
        setCandidacyInfo({
          candidacyId,
          status: CandidacyStatus.OFFER,
          lastStatusChangeByAgent: false,
        });
        onStatusChanged?.(CandidacyStatus.OFFER);
        form.reset();
        onOpenChange(false);
      },
    });
  };

  // Only want to show live/backfill requisitions that don't currently have a placement in the offered or accepted state.
  // requisitions is already only Live/Backfill Reqs
  const requisitionsOpenToPlacement = requisitions.filter(
    (r) =>
      !r.placements.some((p) =>
        [PlacementStatus.OFFERED, PlacementStatus.ACCEPTED].includes(p.status),
      ),
  );

  return (
    <Dialog
      onOpenChange={(open) => {
        form.reset();
        onOpenChange(open);
        setSelectedRequisition(undefined);
      }}
      open={open}
    >
      <DialogContent className="max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Fill out information about the job offer</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            className="flex flex-col gap-4"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <ChangeStatusDialogRequisitionSelect
              fieldName="requisitionId"
              form={form}
              onRequisitionChange={(requisition?: Requisition) => {
                handleFunctions.handleRequisitionChange({
                  form,
                  job,
                  requisition,
                  setSelectedRequisition,
                });
              }}
              // Only want to show live/backfill requisitions that don't currently have a placement in the offered or accepted state.
              requisitions={requisitionsOpenToPlacement}
            />
            <FormNumberSelect
              disabled={!selectedRequisition}
              fieldName="ownerUserId"
              form={form}
              label="Owner"
              options={userOptions}
            />
            <FormEnumSelect
              disabled={!selectedRequisition}
              fieldName="shiftType"
              form={form}
              label="Shift type"
              // Only want to show shift types that are allowed for the selected requisition
              options={shiftTypeOptions.filter((option) =>
                selectedRequisition?.shiftType.includes(option.value),
              )}
            />
            <FormDateInput
              disabled={!selectedRequisition}
              fieldName="dateOffered"
              form={form}
              label="Date offered"
            />
            <FormCurrencyInput
              disabled={!selectedRequisition}
              fieldName="baseCompensation"
              form={form}
              label={`Compensation ($${job.compensationType === CompensationType.HOURLY ? "/hr" : "/yr"})`}
              onChange={() => {
                handleFunctions.handleCompensationChange({
                  form,
                  job,
                  requisition: selectedRequisition,
                });
              }}
            />
            <FormCurrencyInput
              disabled={
                !selectedRequisition ||
                selectedRequisition.status === RequisitionStatus.BACKFILL ||
                job.revenueType === RevenueType.PERCENT
              }
              fieldName="revenueEarned"
              form={form}
              // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
              label={`Revenue earned (${job.revenueType === RevenueType.PERCENT ? `${job.percentOfSalary ?? 0}% of annualized salary` : "fixed"})`}
              // Revenue Type can't be changed on a job that has placements, so this field should be filled for percent revenue type.
              optional={job.revenueType !== RevenueType.PERCENT}
              placeholder={
                selectedRequisition &&
                selectedRequisition.status === RequisitionStatus.BACKFILL
                  ? "$0"
                  : undefined
              }
            />
            <FormNumberInput
              disabled
              fieldName="backfillGuaranteeDays"
              form={form}
              label="Backfill guarantee days (must be edited from job form)"
            />
            <FormDateInput
              disabled={!selectedRequisition}
              fieldName="dateSetToStart"
              form={form}
              label="Date set to start"
              optional
            />
            <FormTextInput
              fieldName="shiftTypeDescription"
              form={form}
              label="Shift type description (details about shift type)"
              optional
            />
            <FormCurrencyInput
              disabled={
                !selectedRequisition ||
                job.compensationType !== CompensationType.HOURLY
              }
              fieldName="shiftDifferential"
              form={form}
              label="Shift differential"
              optional
              placeholder={
                job.compensationType === CompensationType.HOURLY
                  ? "$0 /hr"
                  : "N/A"
              }
            />
            <FormNumberInput
              disabled={!selectedRequisition}
              fieldName="retentionBonusDays"
              form={form}
              label="Retention bonus days"
              optional
            />
            <FormCurrencyInput
              disabled={!selectedRequisition}
              fieldName="retentionBonusAmount"
              form={form}
              label="Retention bonus amount ($$)"
              optional
            />
            <Row className="justify-end">
              <Button className="rounded-full" type="submit">
                Submit
              </Button>
            </Row>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
